import React, { useState, useEffect } from 'react';
import { UpgradePromptData, SubscriptionPlan } from '../types/api';
import apiClient from '../services/api';
import './UpgradePrompt.css';

interface UpgradePromptProps {
  data: UpgradePromptData;
  onClose: () => void;
  onUpgrade?: () => void;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({ data, onClose, onUpgrade }) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const isAddon = data.suggested_plan === 'ai_credits';

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const fetchedPlans = await apiClient.getActiveSubscriptionPlans();
      setPlans(fetchedPlans);
    } catch (error) {
      console.error('Failed to load subscription plans:', error);
    }
  };

  const getSuggestedPlan = (): SubscriptionPlan | undefined => {
    const planName = data.suggested_plan === 'ai_credits' ? 'AI credits' : data.suggested_plan;
    return plans.find(p => p.plan_name.toLowerCase() === planName.toLowerCase());
  };

  const getLimitText = () => {
    switch (data.type) {
      case 'chapter_limit':
        return `You've reached your chapter limit of ${data.limits.limit} chapters.`;
      case 'ai_usage_limit':
        return `You've reached your AI usage limit of ${data.limits.limit} requests.`;
      default:
        return 'You\'ve reached your plan limit.';
    }
  };

  const getPlanName = (plan: string) => {
    if (plan === 'ai_credits') return 'AI Credits Pack';
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const getPlanColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'premium': return '#7c3aed';
      case 'ai_credits': return '#2158e8';
      default: return '#2158e8';
    }
  };

  const getPlanPrice = () => {
    const plan = getSuggestedPlan();
    if (plan) {
      // For addons (one-time purchase), use monthly price
      if (plan.is_addon) {
        return `$${plan.price_monthly} one-time`;
      }
      return `$${plan.price_monthly}/month`;
    }
    // Fallback
    switch (data.suggested_plan.toLowerCase()) {
      case 'premium': return '$19/month';
      case 'ai_credits': return '$5 one-time';
      default: return 'Contact us';
    }
  };

  const getAICreditsAmount = () => {
    const plan = getSuggestedPlan();
    return plan?.ai_usage_limit || 100;
  };

  const getCurrentPlan = (): SubscriptionPlan | undefined => {
    return plans.find(p => p.plan_name.toLowerCase() === data.current_plan.toLowerCase());
  };

  const getCurrentPlanPrice = () => {
    const plan = getCurrentPlan();
    if (plan) {
      return `$${plan.price_monthly}/month`;
    }
    return '';
  };

  return (
    <div className="upgrade-prompt-overlay">
      <div className="upgrade-prompt">
        <div className="upgrade-header">
          <h2>{isAddon ? 'Get More AI Credits' : 'Upgrade Your Plan'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="upgrade-content">
          <div className="limit-info">
            <p className="limit-message">{getLimitText()}</p>
            <p className="upgrade-message">{data.message}</p>
          </div>

          <div className="suggested-plan">
            <h3>{isAddon ? 'Recommended Add-on' : 'Recommended Plan'}</h3>
            <div
              className="plan-card recommended"
              style={{ borderColor: getPlanColor(data.suggested_plan) }}
            >
              <div className="plan-header">
                <h4
                  className="plan-title"
                  style={{ color: getPlanColor(data.suggested_plan) }}
                >
                  {getPlanName(data.suggested_plan)}
                </h4>
                <div className="plan-price">{getPlanPrice()}</div>
              </div>

              <div className="plan-benefits">
                <h5>What you'll get:</h5>
                <ul>
                  {data.suggested_plan === 'premium' && (
                    <>
                      <li>Unlimited chapters</li>
                      <li>Unlimited AI requests</li>
                      <li>Priority support</li>
                      <li>Advanced export options</li>
                      <li>Collaboration features</li>
                    </>
                  )}
                  {data.suggested_plan === 'ai_credits' && (
                    <>
                      <li>{getAICreditsAmount()} additional AI credits</li>
                      <li>AI image generation</li>
                      <li>Chat with Memoir</li>
                      <li>No expiration</li>
                      <li>Use anytime</li>
                    </>
                  )}
                </ul>
              </div>
            </div>

            <h3 className="current-plan-heading">Current Plan:</h3>
            <div className="plan-card current">
              <div className="plan-header">
                <h4 className="plan-title">{getPlanName(data.current_plan)}</h4>
                <div className="plan-price">{getCurrentPlanPrice()}</div>
              </div>
              <div className="plan-limits">
                <p>{data.limits.limit} AI requests included</p>
              </div>
            </div>
          </div>
        </div>

        <div className="upgrade-actions">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Maybe Later
          </button>
          <button
            className="btn btn-primary upgrade-btn"
            onClick={onUpgrade}
            style={{ backgroundColor: getPlanColor(data.suggested_plan) }}
          >
            {isAddon ? 'Purchase AI Credits' : `Upgrade to ${getPlanName(data.suggested_plan)}`}
          </button>
        </div>

        <div className="upgrade-footer">
          <p>Unlock your creative potential!</p>
          {!isAddon && <small>You can cancel anytime. No hidden fees.</small>}
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
